/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import https from 'https';
import getConfig from '../../config';
import uuid from 'uuid';
declare let adobeIMS: AdobeIMS;
import {AppHomeUCSData} from '../containers/AppHome/types';
import {fakeIllustratorUCSResponse} from '../containers/AppHome/appHomeData';
class UserContentService {
	/*
	 **
	 **  P U B L I C   I N T E R F A C E
	 **
	 */
	fetchQuicklinks() {
		return this._performQuicklinksFetch();
	}

	async fetchAppHomes(appContextType: string): Promise<AppHomeUCSData | Error> {
		const response = await this._performAppHomesFetch(appContextType);
		if (!(response instanceof Error)) {
			if (appContextType === 'illustratorweb' && window.cchome.util.hasFeature('fake_illustrator')) {
				return fakeIllustratorUCSResponse.fakeResponse;
			}
			// response = fakeData; // TODO: remove this when I can deploy UCS
			response.appContextType = appContextType;
		}
		return response;
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */
	static instance: UserContentService;

	// $FlowFixMe
	constructor() {
		if (!UserContentService.instance) {
			UserContentService.instance = this;
		}
		return UserContentService.instance;
	}

	_performQuicklinksFetch(): Promise<any> {
		return new Promise((resolve, reject) => {
			const {hostname, quicklinksPath} = getConfig('SERVICES').ucs;
			const hasQuickLinksPatternCard
				= window.cchome.util.hasFeature('new_quickLink_card')
				&& window.cchome.util.hasFeature('QuickLinks_Pattern');

			const obj = {
				hostname,
				path: `${quicklinksPath}?locale=${window.cchome.util.getLocale()}&&hasPattern=${hasQuickLinksPatternCard}`,
				method: 'GET',
				headers: {
					Authorization: `Bearer ${adobeIMS.getAccessToken()}`,
					'x-api-key': 'CCHomeWeb1',
					'x-request-id': uuid.v4(),
					'Accept-Language': 'en',
					'Content-Type': 'application/json',
				},
				dataType: 'json',
				withCredentials: false,
			};
			const request = https.request(obj, (res) => {
				// Report status to the user content store
				if (res.statusCode && res.statusCode >= 300) {
					window.cchome.util.logger.error(
						'UserContent Service',
						'Problem with UserContentService request',
						res,
					);
					reject(new Error('UserContentService error'));
				}
				else {
					let data = '';
					// set the encoding to UTF8 to prevent decoding issues on certain browsers.
					// https://jira.corp.adobe.com/browse/CCH-4427
					res.setEncoding('utf8');
					res.on('data', chunk => (data += chunk));
					res.on('end', () => {
						// Success!
						try {
							resolve(JSON.parse(data));
						}
						catch (e: any) {
							window.cchome.util.logger.error(
								'User Content Service',
								'Problem with parsing UserContentService response',
								e.toString(),
							);
							reject(new Error('UserContentService parse error'));
						}
					});
				}
			});

			request.on('error', (err) => {
				// There was a connection error of some sort
				window.cchome.util.logger.error(
					'User Content Service',
					'Problem with UserContentService request',
					err.toString(),
				);
				reject(new Error('UserContentService error'));
			});
			request.end();
		});
	}

	/**
	 * Fetches the data for rendering App Homes from UCS.
	 *
	 * @param {string} appContextType
	 * @returns
	 */
	_performAppHomesFetch(appContextType: string): Promise<any> {
		return new Promise((resolve, reject) => {
			const {hostname, apphomesPath} = getConfig('SERVICES').ucs;

			const obj = {
				hostname,
				// path: `/ucs/v3/users/me/surfaces/cchome/contents/quicklinks/context/home?locale=en_US&hasPattern=true`,
				path: `${apphomesPath}${appContextType}?locale=${window.cchome.util.getLocale()}`,
				// in full: the path is /ucs/v3/users/me/surfaces/cchome/contents/webapphomes/context/${appContextType}?locale={{locale}}
				method: 'GET',
				headers: {
					Authorization: `Bearer ${adobeIMS.getAccessToken()}`,
					'x-api-key': 'CCHomeWeb1',
					'x-request-id': uuid.v4(),
					'Accept-Language': 'en',
					'Content-Type': 'application/json',
				},
				dataType: 'json',
				withCredentials: false,
			};
			const request = https.request(obj, (res) => {
				// Report status to the user content store
				if (res.statusCode && res.statusCode >= 300) {
					window.cchome.util.logger.error(
						'UserContent Service',
						'Problem with UserContentService request',
						res,
					);
					// RESOLVE THE ERROR: We want to return an error to the client
					resolve(new Error('UserContentService error'));
				}
				else {
					let data = '';
					// set the encoding to UTF8 to prevent decoding issues on certain browsers.
					// https://jira.corp.adobe.com/browse/CCH-4427
					res.setEncoding('utf8');
					res.on('data', chunk => (data += chunk));
					res.on('end', () => {
						// Success!
						try {
							const parsedData = JSON.parse(data);
							parsedData.appContextType = appContextType;
							resolve(parsedData);
						}
						catch (e: any) {
							window.cchome.util.logger.error(
								'User Content Service',
								'Problem with parsing UserContentService response',
								e.toString(),
							);
							// RESOLVE THE ERROR: We want to return an error to the client
							resolve(new Error('UserContentService parse error'));
						}
					});
				}
			});

			request.on('error', (err) => {
				// There was a connection error of some sort
				window.cchome.util.logger.error(
					'User Content Service',
					'Problem with UserContentService request',
					err.toString(),
				);
				// RESOLVE THE ERROR: We want to return an error to the client
				resolve(new Error('UserContentService error'));
			});
			request.end();
		});
	}
}

export default UserContentService;
